import { useEffect } from "react";
import type { HocuspocusProvider } from "@hocuspocus/provider";

export const ACTIVITY_AWARENESS_FIELD = "activityV1";
export const ACTIVITY_RENEW_MS = 10_000;
export const ACTIVITY_TTL_MS = 30_000;
export const ACTIVITY_VERSION = 1;

export type PresenceActivity = "viewing" | "editing";
export type PresenceTargetKind = "section" | "file";

export interface PresenceTarget {
    kind: PresenceTargetKind;
    id: string;
}

export interface ActivityAwarenessV1 {
    version: 1;
    target: PresenceTarget;
    activity: PresenceActivity;
    updatedAt: number;
}

const TARGET_ID = /^[1-9]\d*$/;

export function presenceTarget(kind: PresenceTargetKind, id: string | number | null | undefined): PresenceTarget | null {
    const normalized = id == null ? "" : String(id);
    return TARGET_ID.test(normalized) ? { kind, id: normalized } : null;
}

export function parseActivityAwareness(value: unknown, now = Date.now()): ActivityAwarenessV1 | null {
    if (!value || typeof value !== "object") return null;
    const data = value as Record<string, unknown>;
    const target = data.target as Record<string, unknown> | undefined;
    if (
        data.version !== ACTIVITY_VERSION ||
        (data.activity !== "viewing" && data.activity !== "editing") ||
        typeof data.updatedAt !== "number" || !Number.isFinite(data.updatedAt) ||
        data.updatedAt > now + 5_000 || now - data.updatedAt >= ACTIVITY_TTL_MS ||
        !target || (target.kind !== "section" && target.kind !== "file") ||
        typeof target.id !== "string" || !TARGET_ID.test(target.id)
    ) return null;
    return data as unknown as ActivityAwarenessV1;
}

export function useActivityPublisher(
    provider: HocuspocusProvider | null | undefined,
    target: PresenceTarget | null,
    activity: PresenceActivity
): void {
    useEffect(() => {
        if (!provider || !target) return;
        const publish = () => provider.setAwarenessField(ACTIVITY_AWARENESS_FIELD, {
            version: ACTIVITY_VERSION,
            target,
            activity,
            updatedAt: Date.now()
        } satisfies ActivityAwarenessV1);
        publish();
        const timer = window.setInterval(publish, ACTIVITY_RENEW_MS);
        return () => {
            window.clearInterval(timer);
            provider.setAwarenessField(ACTIVITY_AWARENESS_FIELD, undefined);
        };
    }, [activity, provider, target?.id, target?.kind]);
}
